import { createSilenceState, SILENCE_WARNING_MS, type SilenceState } from './recordingSilence';

export const silenceStorageKey = (scope: string, recordingId: string) =>
  `recording-silence:v1:${encodeURIComponent(scope)}:${encodeURIComponent(recordingId)}`;

export interface SilenceDiagnostics {
  reason: string;
  observedAt: number;
  contextState: string | null;
  recorderState: string | null;
  trackState: string | null;
  trackMuted: boolean | null;
  trackEnabled: boolean | null;
  rms: number | null;
  rmsThreshold: number;
  sampleGapMs: number | null;
  gapCount: number;
  lastResetReason: string | null;
  lastSoundAt: number | null;
}

export const serializeSilence = (
  recordingId: string, state: SilenceState, savedAt = Date.now(), diagnostics?: SilenceDiagnostics,
) => JSON.stringify({ version: 2, recordingId, quietElapsedMs: state.quietElapsedMs,
  phase: state.phase, toastShown: state.toastShown, dismissed: state.dismissed, savedAt, diagnostics });

export const restoreSilence = (
  raw: string | null, recordingId: string, warningAfterMs = SILENCE_WARNING_MS,
): SilenceState | null => {
  if (!raw) return null;
  try {
    const value = JSON.parse(raw);
    if (!value || ![1, 2].includes(value.version) || value.recordingId !== recordingId ||
      !Number.isFinite(value.savedAt) || value.savedAt < 0 ||
      !Number.isFinite(value.quietElapsedMs) || value.quietElapsedMs < 0 ||
      typeof value.toastShown !== 'boolean') return null;
    if (value.version === 1) {
      // Migrate ten-minute/countdown snapshots without reviving automatic stop.
      if (!['monitoring', 'warning', 'stopped'].includes(value.phase) ||
        !Number.isFinite(value.graceElapsedMs) || value.graceElapsedMs < 0 ||
        value.graceElapsedMs > 60000 || value.quietElapsedMs > 600000) return null;
      const quietElapsedMs = Math.min(warningAfterMs, value.quietElapsedMs);
      return { ...createSilenceState(), quietElapsedMs,
        phase: quietElapsedMs >= warningAfterMs ? 'warning' : 'monitoring',
        toastShown: value.phase === 'monitoring' ? false : value.toastShown };
    }
    if (value.quietElapsedMs > warningAfterMs || typeof value.dismissed !== 'boolean' ||
      !['monitoring', 'warning'].includes(value.phase)) return null;
    if (value.phase === 'monitoring' && (value.quietElapsedMs >= warningAfterMs ||
      value.toastShown || value.dismissed)) return null;
    if (value.phase === 'warning' && value.quietElapsedMs !== warningAfterMs) return null;
    return { ...createSilenceState(), quietElapsedMs: value.quietElapsedMs,
      phase: value.phase, toastShown: value.toastShown, dismissed: value.dismissed };
  } catch { return null; }
};

// Web Locks serialize owners across tabs and release automatically on document death.
// Older WebViews fall back to a document-local lock, with a diagnostic from the hook.
const localOwners = new Map<string, Promise<void>>();
export const withSilenceOwnership = async (
  key: string, signal: AbortSignal, run: () => Promise<void>, wait = true,
  onFallback?: () => void,
): Promise<void> => {
  if (typeof navigator !== 'undefined' && navigator.locks) {
    let entered = false;
    const enter = async () => {
      if (signal.aborted) return;
      entered = true;
      await run();
    };
    try {
      if (wait) await navigator.locks.request(key, { signal }, enter);
      else await navigator.locks.request(key, { ifAvailable: true }, async (lock) => {
        if (lock) await enter();
      });
      return;
    } catch (error) {
      if (signal.aborted) return;
      // A blocked lock API may fall back; an error inside the monitor must not start it twice.
      if (entered) throw error;
    }
  }
  onFallback?.();
  if (signal.aborted || (!wait && localOwners.has(key))) return;
  const previous = localOwners.get(key);
  let release!: () => void;
  const held = new Promise<void>((resolve) => { release = resolve; });
  localOwners.set(key, held);
  try {
    await previous;
    if (!signal.aborted) await run();
  } finally {
    release();
    if (localOwners.get(key) === held) localOwners.delete(key);
  }
};

// Reconcile metadata with the existing IndexedDB recovery list, never with wall-clock age.
export const pruneSilenceSnapshots = async (scope: string, recordingIds: string[]): Promise<void> => {
  const prefix = silenceStorageKey(scope, '');
  const valid = new Set(recordingIds.map((id) => silenceStorageKey(scope, id)));
  const stale: string[] = [];
  for (let index = 0; index < window.localStorage.length; index++) {
    const key = window.localStorage.key(index);
    if (key?.startsWith(prefix) && !valid.has(key)) stale.push(key);
  }
  await Promise.all(stale.map((key) => withSilenceOwnership(key, new AbortController().signal,
    async () => { window.localStorage.removeItem(key); }, false)));
};
